'use client';

import classNames from 'classnames';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { Reward, RewardVariant } from '@/app/api';
import { PropsWithClassname } from '@/app/types';
import { useTranslation } from 'react-i18next';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import CrossIcon from '@/app/components/CrossIcon/CrossIcon';
import RewardBox, { RewardBoxStyle } from '@/app/components/RewardBox/RewardBox';
import { Routes } from '@/app/enums';
import { useIsProjectActive } from '@/app/providers/ProjectProvider';

interface RewardPickerDrawerProps extends PropsWithClassname {
  isOpen: boolean;
  onClose: () => void;
  rewards: Reward[];
  selectedVariant?: RewardVariant;
  isDisabled?: boolean;
  numericId: number;
}

const RewardPickerDrawer: FunctionComponent<RewardPickerDrawerProps> = ({
  isOpen,
  onClose,
  rewards,
  selectedVariant,
  isDisabled,
  className,
  numericId,
}) => {
  const { t } = useTranslation();

  const isProjectActive = useIsProjectActive();
  const [isOverflowContainerVisible, setIsOverflowContainerVisible] = useState(false);
  const selectLinkPathname = useMemo<string>(() => `/${Routes.DONATION}/${numericId}`, [numericId]);

  useEffect(() => {
    if (isOpen) {
      setIsOverflowContainerVisible(true);
    } else {
      setTimeout(() => {
        setIsOverflowContainerVisible(false);
      }, 200);
    }
  }, [isOpen]);

  return (
    <div
      className={classNames('overflow-hidden fixed bottom-0 h-[100vh] z-40', {
        'w-full left-0 right-0': isOpen || isOverflowContainerVisible,
      })}
    >
      <div
        className={classNames(
          className,
          'absolute w-full h-full transition-all bg-white px-[24px] overflow-auto flex justify-center',
          {
            'right-[-100%]': !isOpen,
            'right-0': isOpen,
          },
        )}
      >
        <div className="default-page-max-width">
          <div className="py-[16px] lg:px-[16px] flex justify-end">
            <button
              className="flex justify-center lg:translate-x-1/2 items-center w-[44px] lg:w-[48px] h-[44px] lg:h-[48px] border rounded-full border-transparent bg-transparent transition-colors hover:bg-secondary-light hover:border-primary-light"
              onClick={onClose}
              type="button"
            >
              <CrossIcon className="scale-[75%]" />
            </button>
          </div>
          <h2 className="text-center text-[24px] leading-[32px] lg:text-[32px] font-medium lg:leading-[40px] text-balance mb-[26px]">
            {t('components.RewardPickerDrawer.heading')}
          </h2>
          <div>
            {rewards.map((reward) => (
              <RewardBox
                idPrefix="pick"
                key={reward.id}
                reward={reward}
                selectLinkPathname={selectLinkPathname}
                defaultIsOpen
                shouldHideControlButtons={!isProjectActive}
                disabled
                style={RewardBoxStyle.COLUMNS}
                onSelectReward={onClose}
                isVariantSelectVisible
                selectedVariant={selectedVariant}
                isSelectDisabled={isDisabled}
              />
            ))}
          </div>
          <Button
            tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onClose }}
            className={classNames(
              'fixed bottom-[24px] left-1/2 translate-x-[-50%] px-[16px] py-[12px] lg:px-[24px] lg:py-[20px]',
              {
                hidden: !isOpen,
                flex: isOpen,
              },
            )}
          >
            <CrossIcon className="scale-[75%] mr-[12px]" />
            <span className="text-nowrap">{t('components.RewardPickerDrawer.hide')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RewardPickerDrawer;
